import { Module } from 'vuex';
import { RootState, RootGettersReturns } from '../types/index';
import {
  BoardGettersKeys,
  BoardMutationsKeys,
  BoardMutationsPayload,
  BoardActionsKeys,
  BoardState,
  BoardActionsPayload,
  Board as BoardType,
} from '../types/board';
import Session from './session';
import Stage from './stageState';
import Element from './element';
import API from '@/plugins/axios';
import router from '@/router';
import { StageActionsKeys, StageMutationsKeys, StageWithoutDimensions } from '../types/stageState';
import { ElementActionsKeys, ElementMutationsKeys } from '../types/element';
import { SessionMutationsKeys } from '../types/session';
import { ActiveItem, InterfaceActionsKeys } from '../types/interface';
import { AxiosError } from 'axios';

const sortBoards = (boards: BoardType[]) =>
  [...boards].sort((a, b) => {
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

const initState = () => {
  const state: BoardState = {
    board_list: [],
    active_board: null,
    active_shared_board: false,
    favorites: [],
    shared_board_list: [],
    labels: [],
    filter: [],
  };
  return state;
};
const Board: Module<BoardState, RootState> = {
  state: {
    ...initState(),
  },
  actions: {
    [BoardActionsKeys.LIST_BOARDS]: async ({ commit }) => {
      const resp = await API.get('/api/boards/list');
      commit(BoardMutationsKeys['SET_BOARD_LIST'], resp.data.boards);
    },
    [BoardActionsKeys.LIST_SHARED_BOARDS]: async ({ commit }) => {
      const resp = await API.get('/api/user/shared_list');
      commit(BoardMutationsKeys['SET_SHARED_BOARD_LIST'], resp.data.boards);
    },
    [BoardActionsKeys.LIST_FAVORITES]: async ({ commit }) => {
      const resp = await API.get('/api/favorites');
      commit(BoardMutationsKeys['SET_FAVORITES'], resp.data.favorites);
    },
    [BoardActionsKeys.LIST_LABELS]: async ({ commit }) => {
      const resp = await API.get('/api/labels');
      commit(BoardMutationsKeys['SET_LABELS'], resp.data);
    },
    [BoardActionsKeys.CREATE_LABEL]: async ({ commit }, payload: BoardActionsPayload['CREATE_LABEL']) => {
      const { data } = await API.post(`/api/labels`, {
        name: payload,
      });
      commit(BoardMutationsKeys['CREATE_LABEL'], data);
    },
    [BoardActionsKeys.EDIT_LABEL]: async ({ commit }, payload: BoardActionsPayload['EDIT_LABEL']) => {
      await API.patch(`/api/label/${payload.labelId}`, {
        name: payload.name,
      });
      commit(BoardMutationsKeys['EDIT_LABEL'], payload);
    },
    [BoardActionsKeys.DELETE_LABEL]: async ({ commit }, payload: BoardActionsPayload['DELETE_LABEL']) => {
      await API.delete(`/api/label/${payload}`);
      commit(BoardMutationsKeys['DELETE_LABEL'], payload);
    },
    [BoardActionsKeys.APPLY_LABEL]: async ({ commit }, payload: BoardActionsPayload['APPLY_LABEL']) => {
      await API.post(`/api/board/${payload.boardId}/apply_label`, {
        labelId: payload.labelId,
        active: payload.active,
      });
      commit(BoardMutationsKeys['APPLY_LABEL'], payload);
    },
    [BoardActionsKeys.SET_FAVORITE]: async ({ commit }, payload: BoardActionsPayload['SET_FAVORITE']) => {
      const resp = await API.post('/api/favorites', {
        boardId: payload,
      });
      if (resp.data.fav) {
        commit(BoardMutationsKeys['ADD_FAVORITE'], resp.data.fav);
      } else {
        commit(BoardMutationsKeys['REMOVE_FAVORITE'], payload);
      }
    },
    [BoardActionsKeys.SHARE_BOARD]: async ({ commit }, payload: BoardActionsPayload['SHARE_BOARD']) => {
      const { data } = await API.get(`/api/board/share/${payload}`);
      commit(BoardMutationsKeys.SHARE_BOARD, payload);
      return data.uuid;
    },
    [BoardActionsKeys.UNSHARE_BOARD]: async ({ commit }, payload: BoardActionsPayload['SHARE_BOARD']) => {
      await API.get(`/api/board/unshare/${payload}`);
      commit(BoardMutationsKeys.UNSHARE_BOARD, payload);
    },
    [BoardActionsKeys.ACCEPT_SHARED_BOARD]: async (
      { commit, rootGetters },
      payload: BoardActionsPayload['ACCEPT_SHARED_BOARD']
    ) => {
      try {
        const { data } = await API.get(`/api/board/accept_share/${payload}`);
        commit(BoardMutationsKeys.ACCEPT_SHARED_BOARD, data.board);
        return data.board.id;
      } catch (e) {
        if ((e as AxiosError)?.response?.status === 409) {
          const boards = (rootGetters as RootGettersReturns).sharedBoardList;
          const board = boards.find(b => b.uuid === payload);
          if (board) {
            return board.id;
          }
        } else {
          console.error('cant accept');
          return false;
        }
      }
    },
    [BoardActionsKeys.REMOVE_SHARED_BOARD]: async ({ commit }, payload: BoardActionsPayload['REMOVE_SHARED_BOARD']) => {
      try {
        await API.get(`/api/board/remove_share/${payload}`);
        commit(BoardMutationsKeys.REMOVE_SHARED_BOARD, payload);
      } catch {
        console.error('cant remove');
      }
    },
    [BoardActionsKeys.RENAME_BOARD]: async ({ commit }, payload: BoardActionsPayload['RENAME_BOARD']) => {
      await API.post('/api/board/update', payload);
      commit(BoardMutationsKeys['RENAME_BOARD'], payload);
    },
    [BoardActionsKeys.DUPLICATE_BOARD]: async ({ commit }, payload: BoardActionsPayload['DUPLICATE_BOARD']) => {
      const { data } = await API.get(`/api/board/saveas/${payload.boardId}`);
      commit(BoardMutationsKeys['ADD_BOARD'], data.board);
    },
    [BoardActionsKeys.OPEN_BOARD]: async ({ commit, dispatch }, payload: BoardActionsPayload['OPEN_BOARD']) => {
      const resp = await API.get(`/api/board/${payload.id}`);
      commit(BoardMutationsKeys['OPEN_BOARD'], { board: resp.data.board, shared: payload.shared });
      let stage: StageWithoutDimensions = {
        x: 0,
        y: 0,
        zoom: 1,
      };
      if (resp.data.stage) {
        stage = resp.data.stage.data;
      }
      dispatch(InterfaceActionsKeys.CHANGE_ACTIVE_ITEM, ActiveItem.POINTER);
      dispatch(InterfaceActionsKeys.CHANGE_LASER_ACTIVE, false);
      commit(StageMutationsKeys.STAGE_RESET_STATE);
      dispatch(StageActionsKeys.SET_STAGE, stage);
      commit(ElementMutationsKeys.ELEMENT_RESET_STATE);
      dispatch(ElementActionsKeys.LOAD_ELEMENTS);
      router.push('/');
    },
    [BoardActionsKeys.CLOSE_BOARD]: async ({ commit }) => {
      commit(BoardMutationsKeys['CLOSE_BOARD']);
      router.push('/dashboard');
    },
    [BoardActionsKeys.CREATE_BOARD]: async ({ commit }) => {
      const resp = await API.post('/api/board/create');
      commit(BoardMutationsKeys.ADD_BOARD, resp.data.board);
      return resp.data.board;
    },
    [BoardActionsKeys.DELETE_BOARD]: async ({ commit }, payload: BoardActionsPayload['DELETE_BOARD']) => {
      await API.delete(`/api/board/${payload.boardId}`);
      commit(BoardMutationsKeys.DELETE_BOARD, { boardId: payload.boardId });
      commit(SessionMutationsKeys.REMOVE_SESSIONS_FOR_BOARD, payload.boardId);
    },
    [BoardActionsKeys.SET_FILTER]: ({ commit }, payload: BoardActionsPayload['SET_FILTER']) => {
      commit(BoardMutationsKeys.SET_FILTER, payload);
    },
    [BoardActionsKeys.RESET_STATE]: ({ commit }) => {
      commit(BoardMutationsKeys.BOARD_RESET_STATE);
    },
  },
  mutations: {
    [BoardMutationsKeys.SET_BOARD_LIST]: (state, payload: BoardMutationsPayload['SET_BOARD_LIST']) => {
      state.board_list = sortBoards(payload);
    },
    [BoardMutationsKeys.SET_SHARED_BOARD_LIST]: (state, payload: BoardMutationsPayload['SET_SHARED_BOARD_LIST']) => {
      state.shared_board_list = sortBoards(payload);
    },
    [BoardMutationsKeys.SET_FAVORITES]: (state, payload: BoardMutationsPayload['SET_FAVORITES']) => {
      state.favorites = payload;
    },
    [BoardMutationsKeys.SET_LABELS]: (state, payload: BoardMutationsPayload['SET_LABELS']) => {
      state.labels = payload;
    },
    [BoardMutationsKeys.CREATE_LABEL]: (state, payload: BoardMutationsPayload['CREATE_LABEL']) => {
      state.labels = [...state.labels, payload];
    },
    [BoardMutationsKeys.EDIT_LABEL]: (state, payload: BoardMutationsPayload['EDIT_LABEL']) => {
      const label = state.labels.find(l => l.id === payload.labelId);
      if (label) {
        label.name = payload.name;
        state.labels = [...state.labels];
      }
      state.board_list = state.board_list.map(b => {
        const boardLabel = b.labels.find(l => l.id == payload.labelId);
        if (boardLabel) {
          boardLabel.name = payload.name;
          b.labels = [...b.labels];
        }
        return b;
      });
      state.shared_board_list = state.shared_board_list.map(b => {
        const sharedLabel = b.labels.find(l => l.id == payload.labelId);
        if (sharedLabel) {
          sharedLabel.name = payload.name;
          b.labels = [...b.labels];
        }
        return b;
      });
    },
    [BoardMutationsKeys.DELETE_LABEL]: (state, payload: BoardMutationsPayload['DELETE_LABEL']) => {
      state.labels = [...state.labels].filter(l => l.id !== payload);
      state.board_list = state.board_list.map(b => {
        b.labels = b.labels.filter(l => l.id !== payload);
        return b;
      });
      state.shared_board_list = state.shared_board_list.map(b => {
        b.labels = b.labels.filter(l => l.id !== payload);
        return b;
      });
    },
    [BoardMutationsKeys.APPLY_LABEL]: (state, payload: BoardMutationsPayload['APPLY_LABEL']) => {
      const board = state.board_list.find(b => b.id == payload.boardId);
      if (!payload.active && board) {
        board.labels = board.labels.filter(l => l.id !== payload.labelId);
      } else {
        const label = state.labels.find(l => l.id == payload.labelId);
        if (label) board?.labels.push(label);
      }
      state.board_list = [...state.board_list];
      const sharedBoard = state.shared_board_list.find(b => b.id == payload.boardId);
      if (!payload.active && sharedBoard) {
        sharedBoard.labels = sharedBoard.labels.filter(l => l.id !== payload.labelId);
      } else {
        const label = state.labels.find(l => l.id == payload.labelId);
        if (label) sharedBoard?.labels.push(label);
      }
      state.shared_board_list = [...state.shared_board_list];
    },
    [BoardMutationsKeys.ADD_BOARD]: (state, payload: BoardMutationsPayload['ADD_BOARD']) => {
      state.board_list = [payload, ...state.board_list];
    },
    [BoardMutationsKeys.DELETE_BOARD]: (state, payload: BoardMutationsPayload['DELETE_BOARD']) => {
      state.board_list = [...state.board_list.filter(b => b.id !== payload.boardId)];
      state.favorites = [...state.favorites.filter(f => f !== payload.boardId)];
    },
    [BoardMutationsKeys.ADD_FAVORITE]: (state, payload: BoardMutationsPayload['ADD_FAVORITE']) => {
      state.favorites = [...state.favorites, payload.board.id];
    },
    [BoardMutationsKeys.SHARE_BOARD]: (state, payload: BoardMutationsPayload['SHARE_BOARD']) => {
      const board = state.board_list.find(b => b.id === payload);
      if (board) board.shareable = true;
    },
    [BoardMutationsKeys.UNSHARE_BOARD]: (state, payload: BoardMutationsPayload['UNSHARE_BOARD']) => {
      const board = state.board_list.find(b => b.id === payload);
      if (board) board.shareable = false;
    },
    [BoardMutationsKeys.ACCEPT_SHARED_BOARD]: (state, payload: BoardMutationsPayload['ACCEPT_SHARED_BOARD']) => {
      state.shared_board_list.push(payload);
    },
    [BoardMutationsKeys.REMOVE_SHARED_BOARD]: (state, payload: BoardMutationsPayload['REMOVE_SHARED_BOARD']) => {
      state.shared_board_list = state.shared_board_list.filter(b => b.id !== payload);
    },
    [BoardMutationsKeys.REMOVE_FAVORITE]: (state, payload: BoardMutationsPayload['REMOVE_FAVORITE']) => {
      state.favorites = state.favorites.filter(f => f !== payload);
    },
    [BoardMutationsKeys.CLOSE_BOARD]: state => {
      state.active_board = null;
    },
    [BoardMutationsKeys.RENAME_BOARD]: (state, payload: BoardMutationsPayload['RENAME_BOARD']) => {
      if (state.active_board && state.active_board.id === payload.boardId) {
        state.active_board.name = payload.name;
      }

      const board = state.board_list.find(b => b.id === payload.boardId);
      if (board) board.name = payload.name;
    },
    [BoardMutationsKeys.SET_FILTER]: (state, payload: BoardMutationsPayload['SET_FILTER']) => {
      if (payload) {
        if (state.filter.includes(payload)) {
          state.filter = [...state.filter].filter(f => payload !== f);
        } else {
          state.filter = [...state.filter, payload];
        }
      } else {
        state.filter = [];
      }
    },
    [BoardMutationsKeys.OPEN_BOARD]: (state, payload: BoardMutationsPayload['OPEN_BOARD']) => {
      state.active_board = payload.board;
      state.active_shared_board = payload.shared;
    },
    [BoardMutationsKeys.BOARD_RESET_STATE]: state => {
      Object.assign(state, initState());
    },
  },
  getters: {
    [BoardGettersKeys.isBoardOpen]: state => !!state.active_board,
    [BoardGettersKeys.boardName]: state => state.active_board?.name,
    [BoardGettersKeys.boardList]: state => state.board_list,
    [BoardGettersKeys.boardId]: state => state.active_board?.id,
    [BoardGettersKeys.sharedBoard]: state => state.active_shared_board,
    [BoardGettersKeys.favorites]: state => state.favorites,
    [BoardGettersKeys.labels]: state => state.labels,
    [BoardGettersKeys.boardFilter]: state => state.filter,
    [BoardGettersKeys.sharedBoardList]: state => state.shared_board_list,
  },
  modules: {
    Session,
    Stage,
    Element,
  },
};

export default Board;
